import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { passwordSchema } from '@edytech/shared/validators';
import { Card, CardContent, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/Button';
import { Avatar } from '../../components/ui/Avatar';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../lib/api-client';
import { useToast } from '../../components/ui/ToastProvider';
import { User, Mail, Phone, Lock, Camera, Save, Eye, EyeOff } from 'lucide-react';
import { AddressBook } from './AddressBook';

// Profile update schema
const profileSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

// Password change schema
const passwordChangeSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required').max(128),
  newPassword: passwordSchema,
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type PasswordFormData = z.infer<typeof passwordChangeSchema>;

export const ProfilePage: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const { showToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({ current: false, new: false, confirm: false });

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors },
    reset: resetProfile,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordChangeSchema),
  });

  const onProfileSubmit = async (data: ProfileFormData) => {
    setIsLoading(true);
    try {
      await updateProfile(data);
      showToast('Profile updated successfully', 'success');
      setIsEditing(false);
    } catch {
      showToast('Failed to update profile', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordSubmit = async (data: PasswordFormData) => {
    setIsLoading(true);
    try {
      // Call API to change password
      await apiClient.put('/auth/password', {
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
      });
      showToast('Password changed successfully', 'success');
      setIsChangingPassword(false);
      resetPassword();
    } catch {
      showToast('Failed to change password', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    resetProfile({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-[rgb(var(--foreground))]">Profile Settings</h1>

      {/* Profile Info Card */}
      <Card>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <User className="h-5 w-5 text-primary" />
            <span>Personal Information</span>
          </div>
          {!isEditing && (
            <Button variant="secondary" size="sm" onClick={() => setIsEditing(true)}>
              Edit Profile
            </Button>
          )}
        </CardTitle>
        <CardContent>
          <div className="flex flex-col items-center space-y-6 sm:flex-row sm:space-x-6 sm:space-y-0">
            <div className="relative">
              <Avatar name={user?.name || 'User'} size="xl" />
              <button className="absolute bottom-0 right-0 rounded-full bg-primary p-1.5 text-white hover:bg-primary-dark transition-colors">
                <Camera className="h-4 w-4" />
              </button>
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmitProfile(onProfileSubmit)} className="flex-1 space-y-4">
                <Input
                  label="Full Name"
                  placeholder="John Doe"
                  icon={<User className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
                  {...registerProfile('name')}
                  error={profileErrors.name?.message}
                />
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="you@example.com"
                  icon={<Mail className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
                  {...registerProfile('email')}
                  error={profileErrors.email?.message}
                />
                <Input
                  label="Phone Number"
                  type="tel"
                  placeholder="+233 XX XXX XXXX"
                  icon={<Phone className="h-4 w-4 text-[rgb(var(--light-secondary))]" />}
                  {...registerProfile('phone')}
                  error={profileErrors.phone?.message}
                />
                <div className="flex space-x-3">
                  <Button type="submit" variant="primary" disabled={isLoading}>
                    <Save className="mr-2 h-4 w-4" />
                    {isLoading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex-1 space-y-3">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-[rgb(var(--light-secondary))]" />
                  <span className="font-medium">{user?.name}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-4 w-4 text-[rgb(var(--light-secondary))]" />
                  <span>{user?.email}</span>
                  {user?.emailVerified && (
                    <span className="text-xs text-green-500">✓ Verified</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-[rgb(var(--light-secondary))]" />
                  <span>{user?.phone || 'No phone number'}</span>
                  {user?.phoneVerified && (
                    <span className="text-xs text-green-500">✓ Verified</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-[rgb(var(--light-secondary))]">
                    Member since {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Change Password Card */}
      <Card>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Lock className="h-5 w-5 text-primary" />
            <span>Change Password</span>
          </div>
          {!isChangingPassword && (
            <Button variant="secondary" size="sm" onClick={() => setIsChangingPassword(true)}>
              Change Password
            </Button>
          )}
        </CardTitle>
        <CardContent>
          {isChangingPassword ? (
            <form onSubmit={handleSubmitPassword(onPasswordSubmit)} className="space-y-4">
              <Input
                label="Current Password"
                type={visiblePasswords.current ? 'text' : 'password'}
                placeholder="••••••••"
                {...registerPassword('currentPassword')}
                error={passwordErrors.currentPassword?.message}
                endAdornment={<button type="button" aria-label={visiblePasswords.current ? 'Hide password' : 'Show password'} onClick={() => setVisiblePasswords((state) => ({ ...state, current: !state.current }))}>{visiblePasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
              />
              <Input
                label="New Password"
                type={visiblePasswords.new ? 'text' : 'password'}
                placeholder="••••••••"
                {...registerPassword('newPassword')}
                error={passwordErrors.newPassword?.message}
                endAdornment={<button type="button" aria-label={visiblePasswords.new ? 'Hide password' : 'Show password'} onClick={() => setVisiblePasswords((state) => ({ ...state, new: !state.new }))}>{visiblePasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
              />
              <Input
                label="Confirm New Password"
                type={visiblePasswords.confirm ? 'text' : 'password'}
                placeholder="••••••••"
                {...registerPassword('confirmPassword')}
                error={passwordErrors.confirmPassword?.message}
                endAdornment={<button type="button" aria-label={visiblePasswords.confirm ? 'Hide password' : 'Show password'} onClick={() => setVisiblePasswords((state) => ({ ...state, confirm: !state.confirm }))}>{visiblePasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
              />
              <div className="flex space-x-3">
                <Button type="submit" variant="primary" disabled={isLoading}>
                  {isLoading ? 'Updating...' : 'Update Password'}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setIsChangingPassword(false);
                    resetPassword();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-sm text-[rgb(var(--light-secondary))]">
              Change your password to keep your account secure.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Address Book */}
      <AddressBook />
    </div>
  );
};